import { fromBase64Url } from '../api/ids';

export interface DecodedJwt {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
}

function decodeSegment(segment: string, name: string): Record<string, unknown> {
  let text: string;
  try {
    text = new TextDecoder().decode(fromBase64Url(segment));
  } catch {
    throw new Error(`Invalid JWT: ${name} is not base64url`);
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JWT: ${name} is not JSON`);
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`Invalid JWT: ${name} is not an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Decodes a compact JWS (`header.payload.signature`) without verifying the signature.
 * The browser never verifies token signatures: the API is the resource server and validates
 * tokens against the issuer's JWKS (ADR-0005). Claims are used for display and routing only.
 */
export function decodeJwt(token: string): DecodedJwt {
  if (typeof token !== 'string') throw new Error('Invalid JWT: not a string');
  const parts = token.split('.');
  if (parts.length !== 3 && parts.length !== 5) throw new Error('Invalid JWT: expected 3 segments');
  const [h, p] = parts;
  if (!h || !p) throw new Error('Invalid JWT: empty segment');
  return { header: decodeSegment(h, 'header'), payload: decodeSegment(p, 'payload') };
}

/** Returns the `exp` claim in epoch milliseconds, or null. */
export function jwtExpiresAt(token: string): number | null {
  try {
    const exp = decodeJwt(token).payload.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}
